/**
 * FollowUpsScreen — Soren-drafted collection emails (approval-gated).
 * Route: /aria/follow-ups
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { fetchFollowUpQueue, fetchLienAlerts } from '../../api/dashboard'
import { fetchFollowUpDraft, saveFollowUpRecipient, type FollowUpDraft, type AriaBillingStatus } from '../../api/aria'
import { emailPayApp } from '../../api/payApps'
import type { FollowUpItem, LienAlert } from '../../api/types'
import { formatCurrency } from '../../lib/formatters'
import RichTextNotes from '../../components/RichTextNotes'
import { useAuth } from '../../context/AuthContext'
import { track } from '../../lib/track'

const MAILTO_BODY_LIMIT = 1800
const SEND_COOLDOWN_MS = 60_000

function overdueColor(days: number): string {
  if (days >= 30) return 'var(--red)'
  if (days >= 14) return 'var(--amber)'
  return 'var(--ink3)'
}

function lienNoteForProject(alerts: LienAlert[], projectId: number): string | null {
  const match = alerts
    .filter((a) => a.project_id === projectId && a.filed_at == null)
    .sort((a, b) => a.days_remaining - b.days_remaining)[0]
  if (!match) return null
  return `Lien window · ${match.days_remaining}d remaining`
}

function formatDraftFooter(billing: AriaBillingStatus | undefined, cached: boolean): string {
  const base = 'Drafted by Soren · nothing sends without you.'
  if (!billing) return base

  let allowance = ''
  if (billing.mode === 'byo') {
    allowance = 'Powered by your Claude account'
  } else if (billing.is_pro) {
    allowance = 'Soren Pro — unlimited drafts'
  } else if (billing.remaining_monthly != null) {
    allowance = `${billing.remaining_monthly} of ${billing.included_monthly} included drafts left this month · Upgrade to Soren Pro`
  }

  const parts = [allowance, cached ? 'Cached draft (no usage counted)' : null, base].filter(Boolean)
  return parts.join(' · ')
}

function formatReminderChip(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return `reminder sent ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}

async function copyTextWithFallback(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch {
      return false
    }
  }
}

function plainBodyFromHtml(html: string): string {
  return html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n\n').replace(/<[^>]+>/g, ' ').replace(/\s+\n/g, '\n').trim()
}

type SavedDraft = { subject: string; bodyHtml: string; updatedAt: string }

function draftStorageKey(userId: number | undefined, payAppId: number): string {
  return `followup-draft:v1:${userId ?? 'anon'}:${payAppId}`
}

function readSavedDraft(userId: number | undefined, payAppId: number): SavedDraft | null {
  try {
    const raw = localStorage.getItem(draftStorageKey(userId, payAppId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as SavedDraft
    if (!parsed?.bodyHtml) return null
    return parsed
  } catch {
    return null
  }
}

function clearSavedDraft(userId: number | undefined, payAppId: number): void {
  localStorage.removeItem(draftStorageKey(userId, payAppId))
}

function openMailtoUrl(url: string): void {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.rel = 'noopener noreferrer'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

export default function FollowUpsScreen() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const selectedId = searchParams.get('pa') ? parseInt(searchParams.get('pa')!, 10) : null

  const { data: queueData, isLoading: queueLoading } = useQuery({
    queryKey: ['aria-follow-up-queue'],
    queryFn: fetchFollowUpQueue,
  })
  const { data: lienData } = useQuery({
    queryKey: ['lien-alerts'],
    queryFn: fetchLienAlerts,
  })

  const queue = queueData?.queue ?? []
  const lienAlerts = lienData?.alerts ?? []

  const [draft, setDraft] = useState<FollowUpDraft | null>(null)
  const [draftError, setDraftError] = useState<string | null>(null)
  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [editing, setEditing] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [sentConfirmation, setSentConfirmation] = useState<{ payAppId: number; to: string; at: Date } | null>(null)
  const [recipientEmail, setRecipientEmail] = useState('')
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null)
  const [sendCooldownUntil, setSendCooldownUntil] = useState(0)
  const failedDraftIds = useRef<Set<number>>(new Set())
  const failureCounts = useRef<Map<number, number>>(new Map())
  const rowRefs = useRef<Map<number, HTMLButtonElement>>(new Map())
  const recentSentAt = useRef<Map<number, number>>(new Map())

  const extraQueueItems = useMemo((): FollowUpItem[] => {
    if (!selectedId || queue.some((q) => q.pay_app_id === selectedId)) return []
    if (!draft?.facts || draft.facts.pay_app_id !== selectedId) return []
    return [{
      pay_app_id: selectedId,
      project_id: draft.facts.project_id,
      project_name: draft.facts.project_name,
      app_number: draft.facts.app_number,
      amount_due: draft.facts.amount_due,
      days_overdue: draft.facts.days_overdue,
      tone: 'gentle',
      owner_email: draft.facts.owner_email,
    }]
  }, [selectedId, queue, draft])

  const displayQueue = useMemo(
    () => [...extraQueueItems, ...queue],
    [extraQueueItems, queue],
  )

  const selectedItem: FollowUpItem | undefined = displayQueue.find(
    (q) => q.pay_app_id === selectedId,
  ) ?? displayQueue[0]

  const draftMutation = useMutation({
    mutationFn: (payAppId: number) => fetchFollowUpDraft(payAppId),
    onMutate: () => {
      setDraft(null)
      setDraftError(null)
      setSendError(null)
    },
    onSuccess: (d, payAppId) => {
      failedDraftIds.current.delete(payAppId)
      failureCounts.current.set(payAppId, 0)
      const saved = readSavedDraft(user?.id, payAppId)
      setDraft(d)
      if (saved) {
        setSubject(saved.subject || d.subject)
        setBodyHtml(saved.bodyHtml)
        setDraftSavedAt(new Date(saved.updatedAt))
      } else {
        setSubject(d.subject)
        setBodyHtml(d.body_html)
        setDraftSavedAt(null)
      }
      setEditing(false)
      setDraftError(null)
      setSendError(null)
    },
    onError: (e: Error, payAppId) => {
      const count = (failureCounts.current.get(payAppId) || 0) + 1
      failureCounts.current.set(payAppId, count)
      if (count >= 2) {
        failedDraftIds.current.add(payAppId)
        setDraftError('Soren couldn\'t draft this one — write it yourself or try again later.')
        setEditing(true)
        setSubject('')
        setBodyHtml('<p></p>')
      } else {
        setDraft(null)
        setDraftError(e.message || 'Could not generate draft.')
      }
      if (/timed out/i.test(e.message)) {
        Sentry.captureMessage('ARIA follow-up draft timeout', {
          level: 'warning',
          extra: { payAppId },
        })
      }
    },
  })

  const sendMutation = useMutation({
    mutationFn: async (item: FollowUpItem) => {
      const lastSent = recentSentAt.current.get(item.pay_app_id)
      if (lastSent && Date.now() - lastSent < SEND_COOLDOWN_MS) {
        throw new Error('Reminder already sent a moment ago.')
      }
      const to = recipientEmail || item.follow_up_recipient_email || item.owner_email || draft?.facts.owner_email
      if (!to) throw new Error('No owner email on file for this project.')
      const combinedBody = draft?.lien_paragraph_html
        ? `${bodyHtml}<div style="margin-top:16px;padding:12px 14px;background:#fffbeb;border-left:3px solid #f59e0b;border-radius:4px">${draft.lien_paragraph_html}</div>`
        : bodyHtml
      await emailPayApp(item.pay_app_id, {
        to_email: to,
        subject,
        body_html: combinedBody,
        aria_followup: true,
      })
      return { to, payAppId: item.pay_app_id }
    },
    onSuccess: ({ to, payAppId }) => {
      const at = new Date()
      recentSentAt.current.set(payAppId, at.getTime())
      setSendCooldownUntil(at.getTime() + SEND_COOLDOWN_MS)
      setSentConfirmation({ payAppId, to, at })
      track('pay_app_sent', { pay_app_id: payAppId })
      setDraft(null)
      setEditing(false)
      clearSavedDraft(user?.id, payAppId)
      setDraftSavedAt(null)
      queryClient.setQueryData<{ queue: FollowUpItem[] }>(['aria-follow-up-queue'], (old) => {
        if (!old?.queue) return old
        return {
          queue: old.queue.map((q) => (
            q.pay_app_id === payAppId
              ? { ...q, last_reminder_sent_at: at.toISOString() }
              : q
          )),
        }
      })
      queryClient.invalidateQueries({ queryKey: ['aria-follow-up-queue'] })
    },
    onError: (e: Error) => setSendError(e.message),
  })

  const loadDraft = useCallback(
    (payAppId: number, { force = false }: { force?: boolean } = {}) => {
      setSearchParams({ pa: String(payAppId) })
      if (sentConfirmation?.payAppId === payAppId) return
      if (!force && failedDraftIds.current.has(payAppId)) return
      if (force) {
        failedDraftIds.current.delete(payAppId)
        failureCounts.current.set(payAppId, 0)
      }
      if (sentConfirmation?.payAppId !== payAppId) {
        setSentConfirmation(null)
      }
      setDraftError(null)
      draftMutation.mutate(payAppId)
    },
    [draftMutation, setSearchParams, sentConfirmation],
  )

  const retryDraft = useCallback(() => {
    if (!selectedItem) return
    loadDraft(selectedItem.pay_app_id, { force: true })
  }, [loadDraft, selectedItem])

  useEffect(() => {
    if (queueLoading || displayQueue.length === 0) return
    const target = selectedId && displayQueue.some((q) => q.pay_app_id === selectedId)
      ? selectedId
      : displayQueue[0].pay_app_id
    if (sentConfirmation?.payAppId === target) return
    if (draft?.facts.pay_app_id === target) return
    if (draftMutation.isPending) return
    if (failedDraftIds.current.has(target) && failureCounts.current.get(target)! >= 2) return
    loadDraft(target)
  }, [queueLoading, displayQueue, selectedId, loadDraft, draft, draftMutation.isPending, sentConfirmation])

  useEffect(() => {
    if (!selectedId) return
    const el = rowRefs.current.get(selectedId)
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selectedId, displayQueue.length])

  useEffect(() => {
    if (!selectedItem) return
    setRecipientEmail(
      selectedItem.follow_up_recipient_email
      ?? selectedItem.owner_email
      ?? '',
    )
  }, [selectedItem?.pay_app_id, selectedItem?.follow_up_recipient_email, selectedItem?.owner_email])

  useEffect(() => {
    if (!selectedItem?.project_id || !recipientEmail.includes('@')) return
    const t = window.setTimeout(() => {
      saveFollowUpRecipient(selectedItem.project_id, { email: recipientEmail }).catch(() => {})
    }, 600)
    return () => window.clearTimeout(t)
  }, [recipientEmail, selectedItem?.project_id])

  useEffect(() => {
    if (!selectedItem || sentConfirmation) return
    if (!subject && !bodyHtml) return
    const t = window.setTimeout(() => {
      localStorage.setItem(draftStorageKey(user?.id, selectedItem.pay_app_id), JSON.stringify({
        subject,
        bodyHtml,
        updatedAt: new Date().toISOString(),
      } satisfies SavedDraft))
      setDraftSavedAt(new Date())
    }, 500)
    return () => window.clearTimeout(t)
  }, [subject, bodyHtml, selectedItem, user?.id, sentConfirmation])

  async function copyDraftToClipboard() {
    const text = `${subject}\n\n${plainBodyFromHtml(bodyHtml)}`
    const ok = await copyTextWithFallback(text)
    setCopyFeedback(ok ? 'Copied ✓' : 'Copy failed')
    window.setTimeout(() => setCopyFeedback(null), 2000)
  }

  async function openMailtoDraft() {
    const to = recipientEmail || selectedItem?.owner_email || ''
    const plain = plainBodyFromHtml(bodyHtml)
    const fullBody = `${plain}${draft?.lien_paragraph_html ? `\n\n${plainBodyFromHtml(draft.lien_paragraph_html)}` : ''}`
    if (fullBody.length > MAILTO_BODY_LIMIT) {
      await copyTextWithFallback(`${subject}\n\n${fullBody}`)
      setToast(`Body copied (${MAILTO_BODY_LIMIT} char limit) — opening mail app with subject only`)
      window.setTimeout(() => setToast(null), 4000)
      openMailtoUrl(`mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}`)
      return
    }
    openMailtoUrl(`mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fullBody)}`)
  }

  const sendBlocked = selectedItem
    ? sendCooldownUntil > Date.now()
    : false

  function handleSkip() {
    if (!selectedItem) return
    setSentConfirmation(null)
    const idx = displayQueue.findIndex((q) => q.pay_app_id === selectedItem.pay_app_id)
    const next = displayQueue[idx + 1] ?? displayQueue[0]
    if (next && next.pay_app_id !== selectedItem.pay_app_id) {
      setDraft(null)
      setDraftError(null)
      loadDraft(next.pay_app_id)
    } else {
      navigate('/dashboard')
    }
  }

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 4px 32px' }}>
      <style>{`
        @keyframes aria-draft-pulse {
          0%, 80%, 100% { opacity: 0.25; transform: scale(0.85); }
          40% { opacity: 1; transform: scale(1); }
        }
        .aria-drafting-dots span {
          display: inline-block;
          width: 6px; height: 6px; margin: 0 2px;
          border-radius: 50%;
          background: var(--ink);
          animation: aria-draft-pulse 1.2s infinite ease-in-out;
        }
        .aria-drafting-dots span:nth-child(2) { animation-delay: 0.15s; }
        .aria-drafting-dots span:nth-child(3) { animation-delay: 0.3s; }
      `}</style>

      <div style={{ marginBottom: 20 }}>
        <button type="button" className="btn b-gh btn-sm" onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', margin: '12px 0 4px' }}>
          Soren follow-ups
        </h1>
        <p style={{ fontSize: 13, color: 'var(--ink4)', margin: 0 }}>
          Review AI-drafted collection emails before anything goes to the owner.
        </p>
      </div>

      {toast && (
        <div style={{ fontSize: 12, color: 'var(--ink2)', marginBottom: 12, padding: '8px 12px', background: 'var(--blue-l)', borderRadius: 'var(--r)' }}>
          {toast}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(260px, 320px) 1fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <div className="panel">
          <div className="panel-h">Overdue queue</div>
          <div style={{ padding: '8px 0' }}>
            {queueLoading && (
              <div style={{ padding: '16px', fontSize: 12, color: 'var(--ink4)' }}>Loading…</div>
            )}
            {!queueLoading && displayQueue.length === 0 && (
              <div style={{ padding: '24px 16px', fontSize: 13, color: 'var(--ink4)', textAlign: 'center' }}>
                No overdue pay applications.
              </div>
            )}
            {displayQueue.map((item) => {
              const active = selectedItem?.pay_app_id === item.pay_app_id
              const lienNote = lienNoteForProject(lienAlerts, item.project_id)
              const reminderChip = formatReminderChip(
                item.last_reminder_sent_at
                ?? (sentConfirmation?.payAppId === item.pay_app_id ? sentConfirmation.at.toISOString() : null),
              )
              return (
                <button
                  key={item.pay_app_id}
                  ref={(el) => {
                    if (el) rowRefs.current.set(item.pay_app_id, el)
                    else rowRefs.current.delete(item.pay_app_id)
                  }}
                  type="button"
                  data-testid={active ? 'followup-queue-row-selected' : 'followup-queue-row'}
                  data-pay-app-id={item.pay_app_id}
                  onClick={() => loadDraft(item.pay_app_id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 16px',
                    border: 'none',
                    borderBottom: '1px solid var(--line)',
                    background: active ? 'var(--blue-l)' : 'transparent',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                    {item.project_name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 2 }}>
                    PA #{item.app_number ?? '—'}
                    {' · '}
                    <span className="mono">{formatCurrency(item.amount_due)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: overdueColor(item.days_overdue) }}>
                      {item.days_overdue}d overdue
                    </span>
                    {reminderChip && (
                      <span className="chip c-gry" data-testid="followup-reminder-sent-chip" style={{ fontSize: 10 }}>
                        {reminderChip}
                      </span>
                    )}
                  </div>
                  {lienNote && (
                    <div style={{ fontSize: 10, color: 'var(--amber)', marginTop: 4 }}>{lienNote}</div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <div className="panel">
          <div className="panel-h">Draft preview</div>
          <div style={{ padding: 20 }} data-testid="followup-draft-panel">
            {sentConfirmation && (
              <div data-testid="followup-sent-confirmation" style={{ textAlign: 'center', padding: '32px 16px' }}>
                <div style={{ fontSize: 32, marginBottom: 12, color: 'var(--green)' }}>✓</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>
                  Sent to {sentConfirmation.to}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 6 }}>
                  {sentConfirmation.at.toLocaleString()}
                </div>
                <button type="button" className="btn b-ol btn-sm" style={{ marginTop: 20 }} onClick={handleSkip}>
                  Next in queue
                </button>
              </div>
            )}

            {!sentConfirmation && draftMutation.isPending && !draftError && (
              <div style={{ fontSize: 13, color: 'var(--ink)' }} data-testid="followup-drafting">
                Soren is drafting
                <span className="aria-drafting-dots" aria-hidden="true">
                  <span /><span /><span />
                </span>
              </div>
            )}
            {!sentConfirmation && draftError && !draftMutation.isPending && (
              <div>
                <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 12, lineHeight: 1.5 }}>
                  {draftError}
                </div>
                <button type="button" className="btn b-ol btn-sm" onClick={retryDraft}>
                  Retry
                </button>
              </div>
            )}
            {!sentConfirmation && (draft || editing) && !draftMutation.isPending && (
              <>
                {draft?.cached && (
                  <div style={{ fontSize: 11, color: 'var(--ink4)', marginBottom: 12 }}>
                    Drafted a few minutes ago — facts unchanged, no new AI spend.
                  </div>
                )}
                {draftSavedAt && !sentConfirmation && (
                  <div data-testid="followup-draft-saved" style={{ fontSize: 11, color: 'var(--ink4)', marginBottom: 12 }}>
                    Draft saved
                  </div>
                )}
                <label style={{ fontSize: 11, color: 'var(--ink4)', display: 'block', marginBottom: 4 }}>
                  To
                </label>
                <input
                  className="fld-i"
                  data-testid="followup-to-email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', fontSize: 13, marginBottom: 16 }}
                  placeholder="Owner contact email"
                />
                <label style={{ fontSize: 11, color: 'var(--ink4)', display: 'block', marginBottom: 4 }}>
                  Subject
                </label>
                <input
                  className="fld-i"
                  data-testid="followup-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', fontSize: 13, marginBottom: 16 }}
                />

                <label style={{ fontSize: 11, color: 'var(--ink4)', display: 'block', marginBottom: 4 }}>
                  Body
                </label>
                {editing ? (
                  <RichTextNotes value={bodyHtml} onChange={setBodyHtml} />
                ) : (
                  <div
                    data-testid="followup-body-preview"
                    style={{
                      border: '1px solid var(--line)',
                      borderRadius: 'var(--r)',
                      padding: 16,
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: 'var(--ink2)',
                      background: 'var(--surface)',
                      minHeight: 160,
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                    {draft?.lien_paragraph_html && (
                      <div
                        style={{
                          marginTop: 16,
                          padding: '12px 14px',
                          background: 'var(--amber-l)',
                          borderLeft: '3px solid var(--amber-border)',
                          borderRadius: 4,
                          fontSize: 12,
                          color: 'var(--ink2)',
                        }}
                        dangerouslySetInnerHTML={{ __html: draft.lien_paragraph_html }}
                      />
                    )}
                  </div>
                )}

                {sendError && (
                  <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 12 }}>{sendError}</div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="btn b-dk btn-lg"
                    data-testid="followup-approve-send"
                    disabled={sendMutation.isPending || sendBlocked || !selectedItem}
                    onClick={() => selectedItem && sendMutation.mutate(selectedItem)}
                  >
                    {sendMutation.isPending ? 'Sending…' : sendBlocked ? 'Reminder already sent a moment ago.' : 'Approve and send'}
                  </button>
                  <button
                    type="button"
                    className="btn b-ol btn-lg"
                    onClick={copyDraftToClipboard}
                    data-testid="followup-copy"
                  >
                    {copyFeedback ?? 'Copy to my email'}
                  </button>
                  <button
                    type="button"
                    className="btn b-ol btn-lg"
                    onClick={() => void openMailtoDraft()}
                    data-testid="followup-mailto"
                  >
                    Open in mail app
                  </button>
                  <button
                    type="button"
                    className="btn b-ol btn-lg"
                    onClick={() => setEditing((v) => !v)}
                  >
                    {editing ? 'Done editing' : 'Edit draft'}
                  </button>
                  <button type="button" className="btn b-gh btn-lg" onClick={handleSkip}>
                    Skip
                  </button>
                </div>
              </>
            )}
          </div>
          <div
            style={{
              padding: '12px 20px',
              borderTop: '1px solid var(--line)',
              fontSize: 11,
              color: 'var(--ink4)',
              background: 'var(--panel)',
            }}
            data-testid="follow-up-draft-footer"
          >
            {formatDraftFooter(draft?.billing, !!draft?.cached)}
          </div>
        </div>
      </div>
    </div>
  )
}
